import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Lock, User, Loader2, ArrowLeft, Check } from "lucide-react";
import { COMPANY } from "../data/mock";
import { sessionApi, setSession } from "../lib/sessionApi";
import { setToken as setAdminToken } from "../lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;

const AccessModal = ({ open, onClose }) => {
  const [mode, setMode] = useState("login"); // login | register | success

  if (!open) return null;

  const handleClose = () => {
    setMode("login");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 animate-fade-in overflow-y-auto" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={handleClose} />
      <div className="relative w-full max-w-md md:max-w-xl bg-[#0a0a0a] border border-arabell-gold/30 p-8 md:p-10 my-8">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/60 hover:text-arabell-gold"
          aria-label="Chiudi"
          data-testid="access-close"
        >
          <X className="w-5 h-5" strokeWidth={1.2} />
        </button>

        <div className="text-center mb-7">
          <img
            src={COMPANY.logoUrl}
            alt="Arabell"
            className="h-14 w-auto mx-auto mb-4"
            style={{ filter: "invert(1) brightness(1.1)" }}
            loading="lazy"
            decoding="async"
          />
          <p className="text-[10px] tracking-[0.4em] uppercase text-arabell-gold mb-3">
            Member &middot; Staff
          </p>
          <h3 className="font-display text-2xl text-white tracking-[0.15em] uppercase">
            {mode === "login" && "Accedi"}
            {mode === "register" && "Richiedi registrazione"}
            {mode === "success" && "Richiesta inviata"}
          </h3>
          <div className="w-12 h-px bg-arabell-gold mx-auto mt-4" />
        </div>

        {mode === "login" && <LoginForm onClose={handleClose} onRegister={() => setMode("register")} />}
        {mode === "register" && (
          <RegistrationForm
            onBack={() => setMode("login")}
            onSubmitted={() => setMode("success")}
          />
        )}
        {mode === "success" && <SuccessPanel onClose={handleClose} />}
      </div>
    </div>
  );
};

// ============================================================
// LOGIN
// ============================================================
const LoginForm = ({ onClose, onRegister }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const data = await sessionApi.login(username.trim(), pwd);
      // Se le credenziali sono admin, il backend ritorna step (enroll/verify) per 2FA.
      // In quel caso reindirizziamo a /admin dove la 2FA si gestisce.
      if (data.step) {
        setUsername(""); setPwd("");
        onClose();
        navigate("/admin");
        return;
      }
      setSession(data.token, { username: data.username, role: data.role, name: data.name, org: data.org });
      // Se è un admin senza 2FA (legacy), salva il token anche nello storage admin
      if (data.role === "admin") {
        setAdminToken(data.token);
      }
      setUsername(""); setPwd("");
      onClose();
      navigate(data.redirect || "/");
    } catch (err) {
      setError(err.message || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-4" data-testid="access-form">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={1.4} />
          <input
            type="text" required placeholder="Username" autoComplete="username"
            value={username} onChange={(e) => setUsername(e.target.value)}
            data-testid="access-username"
            className="w-full bg-transparent border border-white/15 focus:border-arabell-gold text-white placeholder-white/40 text-sm pl-10 pr-4 py-3 outline-none transition-colors"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={1.4} />
          <input
            type="password" required placeholder="Password" autoComplete="current-password"
            value={pwd} onChange={(e) => setPwd(e.target.value)}
            data-testid="access-password"
            className="w-full bg-transparent border border-white/15 focus:border-arabell-gold text-white placeholder-white/40 text-sm pl-10 pr-4 py-3 outline-none transition-colors"
          />
        </div>
        {error && <p data-testid="access-error" className="text-xs text-red-400 text-center pt-1">{error}</p>}
        <button
          type="submit" disabled={loading} data-testid="access-submit"
          className="w-full mt-2 py-3 bg-white text-black text-xs tracking-[0.3em] uppercase hover:bg-arabell-gold transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continua"}
        </button>
      </form>
      <p className="text-[10px] tracking-[0.25em] uppercase text-center text-white/40 mt-6">
        Non sei membro?{" "}
        <button
          type="button"
          onClick={onRegister}
          data-testid="access-register-link"
          className="text-arabell-gold hover:underline tracking-[0.25em] uppercase"
        >
          Richiedi registrazione
        </button>
      </p>
      <p className="text-[9px] tracking-[0.3em] uppercase text-center text-white/25 mt-4">
        <button
          type="button"
          onClick={() => { onClose(); navigate("/admin"); }}
          data-testid="access-admin-link"
          className="hover:text-arabell-gold transition-colors"
        >
          Admin
        </button>
      </p>
    </>
  );
};

// ============================================================
// REGISTRATION FORM
// ============================================================
const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-white/50 mb-1.5">{label}</span>
    <input
      {...props}
      className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm focus:border-arabell-gold outline-none"
    />
  </label>
);

const RegistrationForm = ({ onBack, onSubmitted }) => {
  const [type, setType] = useState("private");
  const [form, setForm] = useState({});
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("Accetta il trattamento dei dati personali (GDPR)"); return; }
    setLoading(true); setError("");
    try {
      const payload = { type, ...form, consent_privacy: true };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      const res = await fetch(`${API}/api/registration-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Errore (${res.status})`);
      onSubmitted();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="register-form">
      <button
        type="button"
        onClick={onBack}
        className="text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-arabell-gold flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3 h-3" /> Torna al login
      </button>

      <p className="text-xs text-white/60 leading-relaxed">
        Compila i dati anagrafici per richiedere l'accesso. La tua richiesta sarà valutata
        da Arabell Group e ti contatteremo a breve.
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button" onClick={() => setType("private")}
          data-testid="register-type-private"
          className={`px-4 py-1.5 text-[10px] tracking-[0.25em] uppercase border ${type === "private" ? "border-arabell-gold text-arabell-gold" : "border-white/15 text-white/60"}`}
        >
          Privato
        </button>
        <button
          type="button" onClick={() => setType("piva")}
          data-testid="register-type-piva"
          className={`px-4 py-1.5 text-[10px] tracking-[0.25em] uppercase border ${type === "piva" ? "border-arabell-gold text-arabell-gold" : "border-white/15 text-white/60"}`}
        >
          Partita IVA
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {type === "private" ? (
          <>
            <Field label="Nome *" required value={form.first_name || ""} onChange={(e) => upd("first_name", e.target.value)} data-testid="reg-first-name" />
            <Field label="Cognome *" required value={form.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} data-testid="reg-last-name" />
            <Field label="Codice Fiscale" value={form.fiscal_code || ""} onChange={(e) => upd("fiscal_code", e.target.value.toUpperCase())} />
            <Field label="Nato a" value={form.birthplace || ""} onChange={(e) => upd("birthplace", e.target.value)} />
            <Field label="Data di nascita (YYYY-MM-DD)" value={form.date_of_birth || ""} onChange={(e) => upd("date_of_birth", e.target.value)} />
          </>
        ) : (
          <>
            <Field label="Ragione sociale *" required value={form.company_name || ""} onChange={(e) => upd("company_name", e.target.value)} data-testid="reg-company-name" />
            <Field label="P.IVA *" required value={form.vat_number || ""} onChange={(e) => upd("vat_number", e.target.value)} data-testid="reg-vat-number" />
            <Field label="Codice Fiscale" value={form.fiscal_code || ""} onChange={(e) => upd("fiscal_code", e.target.value.toUpperCase())} />
            <Field label="PEC" type="email" value={form.pec || ""} onChange={(e) => upd("pec", e.target.value)} />
            <Field label="SDI / Codice destinatario" value={form.sdi_code || ""} onChange={(e) => upd("sdi_code", e.target.value)} />
            <Field label="Settore" value={form.sector || ""} onChange={(e) => upd("sector", e.target.value)} />
            <Field label="Referente" value={form.referent_name || ""} onChange={(e) => upd("referent_name", e.target.value)} />
          </>
        )}
        <Field label="Email *" type="email" required value={form.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid="reg-email" />
        <Field label="Telefono *" required value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} data-testid="reg-phone" />
      </div>

      <div className="border border-white/10 p-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">Indirizzo {type === "piva" ? "sede" : "di residenza"}</p>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="md:col-span-4"><Field label="Via" value={form.street || ""} onChange={(e) => upd("street", e.target.value)} /></div>
          <div className="md:col-span-2"><Field label="N° civico" value={form.street_number || ""} onChange={(e) => upd("street_number", e.target.value)} /></div>
          <div className="md:col-span-3"><Field label="Città" value={form.city || ""} onChange={(e) => upd("city", e.target.value)} /></div>
          <div className="md:col-span-2"><Field label="CAP" value={form.postal_code || ""} onChange={(e) => upd("postal_code", e.target.value)} /></div>
          <div className="md:col-span-1"><Field label="Prov." value={form.province || ""} onChange={(e) => upd("province", e.target.value.toUpperCase().slice(0, 2))} /></div>
        </div>
      </div>

      <label className="block">
        <span className="block text-[10px] tracking-[0.25em] uppercase text-white/50 mb-1.5">Note (opzionale)</span>
        <textarea
          value={form.notes || ""} onChange={(e) => upd("notes", e.target.value)} rows={2}
          placeholder="Servizi di interesse, motivazione..."
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm focus:border-arabell-gold outline-none resize-none"
        />
      </label>

      <label className="flex items-start gap-2.5 text-[11px] text-white/65 leading-relaxed cursor-pointer">
        <input
          type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
          data-testid="reg-consent"
          className="mt-0.5 accent-arabell-gold"
        />
        <span>
          Acconsento al trattamento dei miei dati personali ai sensi del{" "}
          <a href="/privacy" target="_blank" className="text-arabell-gold underline">GDPR</a>{" "}
          per le finalità di valutazione della richiesta di registrazione. *
        </span>
      </label>

      {error && <p className="text-xs text-red-400 text-center">{error}</p>}

      <button
        type="submit" disabled={loading} data-testid="register-submit"
        className="w-full py-3 bg-arabell-gold text-black text-xs tracking-[0.3em] uppercase hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia richiesta"}
      </button>
    </form>
  );
};

// ============================================================
// SUCCESS
// ============================================================
const SuccessPanel = ({ onClose }) => (
  <div className="text-center" data-testid="register-success">
    <div className="w-14 h-14 rounded-full border border-arabell-gold/40 bg-arabell-gold/10 flex items-center justify-center mx-auto mb-5">
      <Check className="w-7 h-7 text-arabell-gold" strokeWidth={1.4} />
    </div>
    <p className="text-white text-sm mb-2 leading-relaxed">
      La tua richiesta è stata <span className="text-arabell-gold">inoltrata correttamente</span>.
    </p>
    <p className="text-white/55 text-xs mb-7 leading-relaxed">
      Il nostro team Arabell la valuterà e ti contatterà al recapito indicato.
      Riceverai le credenziali di accesso al portale solo dopo l'approvazione.
    </p>
    <button
      onClick={onClose}
      className="px-6 py-3 bg-white text-black text-xs tracking-[0.3em] uppercase hover:bg-arabell-gold transition-colors"
    >
      Chiudi
    </button>
  </div>
);

export default AccessModal;
